// React
import { memo, useCallback, useMemo } from "react";

// Servisni sloj
import { PricingAdjustmentType } from "services/api/pricing/schemeTypes";

// UI
import { Button } from "ui/components/Button/Button";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";
import SchemeItemRow from "./components/SchemeItemRow/SchemeItemRow";

// Tipovi
import { SchemeItemsStepProps } from "./types";

/**
 * Stavke grupe — promjene u odnosu na cjenik.
 *
 * Grupa drži promjenu, ne iznos, pa kad se cjenik podigne svi u postotnoj grupi idu gore
 * zajedno s njim. Nabavne cijene se ne nude jer se ne smiju pregaziti.
 */
const SchemeItemsStep: React.FC<SchemeItemsStepProps> = (props: SchemeItemsStepProps) => {
  const { formValues, setFormValues, errors, overridableKeys } = props;

  const keyOptions: DropdownOption[] = useMemo(
    () => overridableKeys.map((code) => ({ value: code, text: code })),
    [overridableKeys]
  );

  const handleKeyChange = useCallback(
    (_e: any, name: string, option: DropdownOption) => {
      const index = Number(name.replace("item-key-", ""));

      setFormValues((previous) => ({
        ...previous,
        items: previous.items.map((item, i) =>
          i === index ? { ...item, keyCode: option.value } : item
        ),
      }));
    },
    [setFormValues]
  );

  const handleTypeChange = useCallback(
    (_e: any, name: string, option: DropdownOption) => {
      const index = Number(name.replace("item-type-", ""));

      setFormValues((previous) => ({
        ...previous,
        items: previous.items.map((item, i) =>
          i === index
            ? { ...item, adjustmentType: option.value as PricingAdjustmentType }
            : item
        ),
      }));
    },
    [setFormValues]
  );

  const handleValueChange = useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;

      const index = Number(name.replace("item-value-", ""));

      setFormValues((previous) => ({
        ...previous,
        items: previous.items.map((item, i) => (i === index ? { ...item, value } : item)),
      }));
    },
    [setFormValues]
  );

  const handleAddItem = useCallback(() => {
    setFormValues((previous) => ({
      ...previous,
      items: [...previous.items, { keyCode: null, adjustmentType: "Percent", value: null }],
    }));
  }, [setFormValues]);

  const handleRemoveItem = useCallback(
    (index: number) => {
      setFormValues((previous) => ({
        ...previous,
        items: previous.items.filter((_, i) => i !== index),
      }));
    },
    [setFormValues]
  );

  return (
    <>
      <PricingStepIntro
        title="Stavke"
        description={
          <>
            Odaberite ključ i koliko se mijenja u odnosu na cjenik. Postotak{" "}
            <strong>prati cjenik</strong>, pa klijent ide gore zajedno s njim; fiksni iznos ne
            prati. Bez ijedne stavke klijent plaća standardne cijene.
          </>
        }
      />

      <div className="p-4 w-full max-w-4xl mx-auto">
        <div className="flex flex-col gap-4">
          {formValues.items.map((item, index) => (
            <SchemeItemRow
              key={index}
              index={index}
              item={item}
              keyOptions={keyOptions}
              errors={errors}
              onKeyChange={handleKeyChange}
              onTypeChange={handleTypeChange}
              onValueChange={handleValueChange}
              onRemove={handleRemoveItem}
            />
          ))}
        </div>

        <div className="mt-4">
          <Button variant="outlined" children="Dodaj stavku" onClick={handleAddItem} />
        </div>

        <p className="text-sm text-slate-500 mt-4">
          Nabavne cijene se ne mogu mijenjati, pa ih ni nema u popisu ključeva.
        </p>
      </div>
    </>
  );
};

export default memo(SchemeItemsStep);
